import { motion } from "framer-motion";
import { ArrowRight, Check, X } from "lucide-react";

const YES = [
  "You are serious about transforming your physique.",
  "You are tired of generic workout and nutrition programs.",
  "You want real accountability from an experienced coaching team.",
  "You want a strategy built around YOUR body, goals and lifestyle.",
  "You are willing to follow a proven system and make the necessary changes.",
  "You want to build more than a physique — you want discipline, confidence and long-term results.",
];

const NO = [
  "You are looking for a quick fix.",
  "You are not willing to follow the plan.",
  "You want results without consistency.",
  "You are looking for a generic program that works for everyone.",
];

const WhoThisIsForSection = () => {
  const scrollToPrograms = (e) => {
    e.preventDefault();
    document.getElementById("coaching-programs")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="who-this-is-for"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#050505] overflow-hidden"
      data-testid="who-this-is-for-section"
    >
      {/* subtle gold vignette */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.08)_0%,transparent_60%)] pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-14 sm:mb-20"
        >
          <p className="text-[#D4AF37] text-[11px] sm:text-xs tracking-[0.4em] uppercase font-bold mb-4">
            Before You Apply
          </p>
          <h2
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-white leading-[0.95] tracking-tight"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            WHO THIS <span className="text-[#D4AF37]">IS FOR</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mb-16 sm:mb-20">
          {/* THIS IS FOR YOU IF */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="border border-[#D4AF37]/40 bg-black/70 backdrop-blur-sm p-7 sm:p-10 relative overflow-hidden"
            data-testid="who-yes-panel"
          >
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#D4AF37] via-[#F1D060] to-[#D4AF37]" />
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center">
                <Check className="w-4 h-4 text-black" strokeWidth={3} />
              </div>
              <h3
                className="text-white text-xl sm:text-2xl tracking-wider uppercase font-bold"
                style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.1em" }}
              >
                This is for you if:
              </h3>
            </div>
            <ul className="space-y-4">
              {YES.map((line, i) => (
                <li key={i} className="flex items-start gap-3 text-white/85 leading-relaxed text-[15px] sm:text-base">
                  <Check className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" strokeWidth={3} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* THIS IS NOT FOR YOU IF */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="border border-white/10 bg-black/50 backdrop-blur-sm p-7 sm:p-10 relative overflow-hidden"
            data-testid="who-no-panel"
          >
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-red-900/60 via-red-700/60 to-red-900/60" />
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-8 h-8 rounded-full border border-red-500/60 flex items-center justify-center">
                <X className="w-4 h-4 text-red-500" strokeWidth={3} />
              </div>
              <h3
                className="text-white/70 text-xl sm:text-2xl tracking-wider uppercase font-bold"
                style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.1em" }}
              >
                This is not for you if:
              </h3>
            </div>
            <ul className="space-y-4">
              {NO.map((line, i) => (
                <li key={i} className="flex items-start gap-3 text-white/60 leading-relaxed text-[15px] sm:text-base">
                  <X className="w-4 h-4 text-red-500/80 mt-1 flex-shrink-0" strokeWidth={3} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Closing CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="text-center"
        >
          <h3
            className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight tracking-tight mb-8"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            READY TO TAKE YOUR PHYSIQUE <br className="hidden sm:block" />
            TO THE <span className="text-[#D4AF37]">NEXT LEVEL?</span>
          </h3>
          <a
            href="#coaching-programs"
            onClick={scrollToPrograms}
            className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-9 sm:px-12 py-4 sm:py-5 text-xs sm:text-sm tracking-[0.3em] uppercase font-bold hover:bg-white transition-all duration-300 shadow-[0_15px_40px_-10px_rgba(212,175,55,0.55)] hover:shadow-[0_18px_48px_-8px_rgba(212,175,55,0.75)] cursor-pointer"
            data-testid="who-view-programs-btn"
          >
            View Coaching Programs
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default WhoThisIsForSection;
